import { parseJson } from '@medicusunion/front-utils';
import twilio from 'twilio-video';
import { SCREEN_SHARE_TRACK_PREFIX } from '@/constants/devices';
import { ChatMessage } from '@/store/features/chat/types';
import { ParticipantSid } from '@/store/features/room/types';
import { selectUserData } from '@/store/features/user/selectors';
import {
  LocalTrack,
  LocalDataTrack,
  LocalAudioTrack,
  LocalVideoTrack,
  RemoteParticipant,
  AttachRemoteTrackParams,
  ParticipantTrackSubscribedParams,
  DataTrackMessageType,
  RemoteVideoTrack,
} from '@/types/call';
import { wait } from '@/utils/wait';
import { store } from '../store';
import { isMediaTrack } from '../utils/track';
import { logger } from './logger';
import { MediaInputManager } from './mediaInputManager';

class LocalCall {
  localElementId: string = 'local-wrapper-element';
  screenShareElementId: string = 'screen-share-element';
  localTracks: LocalTrack[] = [];
  dataTrack: LocalDataTrack | null = null;
  screenTrack: LocalVideoTrack | null = null;
  remoteScreenTrack: RemoteVideoTrack | null = null;
  room: twilio.Room | null = null;
  isSupported = twilio.isSupported;
  devices: MediaInputManager | null = null;

  get localElement() {
    return document.getElementById(this.localElementId);
  }

  get videoTrack(): LocalVideoTrack | undefined {
    return this.localTracks.find(
      (track): track is LocalVideoTrack => track.kind === 'video'
    );
  }

  get audioTrack(): LocalAudioTrack | undefined {
    return this.localTracks.find(
      (track): track is LocalAudioTrack => track.kind === 'audio'
    );
  }

  async init(video = true, audio = true) {
    try {
      this.reset();

      this.localTracks = await twilio.createLocalTracks({
        video,
        audio,
      });
      this.dataTrack = new twilio.LocalDataTrack();
      this.localTracks.push(this.dataTrack);

      this.attachLocalVideo();

      this.devices = new MediaInputManager({
        onAudioChange: this.switchAudio.bind(this),
        onVideoChange: this.switchVideo.bind(this),
      });
      await this.devices.init();
    } catch (error) {
      this.localTracks = [];
      throw error;
    }
  }

  async connect(token: string, roomId: string, onDisconnected: (error: twilio.TwilioError) => void) {
    this.room = await twilio.connect(token, {
      name: roomId,
      tracks: this.localTracks,
    });

    this.room.on('disconnected', (_, error) => {
      this.reset();

      if (error) {
        onDisconnected(error);
      }
    });

    return this.room;
  }

  async enumerateDevices() {
    try {
      //
    } catch (error) {
      logger.error(error);
    }
  }

  getParticipantBySid(sid: ParticipantSid) {
    return this.room?.participants.get(sid);
  }

  onNewParticipant(cb: (participant: RemoteParticipant) => void) {
    this.room?.on('participantConnected', cb);
  }

  onParticipantDisconnected(cb: (participant: RemoteParticipant) => void) {
    this.room?.on('participantDisconnected', cb);
  }

  onParticipantTrackSubscribed(
    params: ParticipantTrackSubscribedParams,
  ) {
    const participant = this.getParticipantBySid(params.sid);
    if (!participant) {
      return;
    }

    const handleTrackSubscribed = (track: twilio.RemoteTrack) => {
      this.attachRemoteTrack({
        ...params,
        track,
      });
    };

    participant.on('trackSubscribed', handleTrackSubscribed);
    participant.tracks.forEach((publication) => {
      if (publication.isSubscribed && publication.track) {
        handleTrackSubscribed(publication.track);
      }
    });
    participant.dataTracks.forEach((publication) => {
      if (publication.track) {
        handleTrackSubscribed(publication.track);
      }
    });
  }

  onParticipantTrackUnsubscribed(sid: ParticipantSid, cb: (kind: string, isScreen?: boolean) => void) {
    const participant = this.getParticipantBySid(sid);
    if (!participant) {
      return;
    }

    const handleTrackUnsubscribed = (track: twilio.RemoteTrack) => {
      this.detachRemoteTrack(track);
      const isScreen = track.name.includes(SCREEN_SHARE_TRACK_PREFIX);

      cb(track.kind, isScreen);

      if (isScreen) {
        this.detachScreenTrack();
      }
    };

    participant.on('trackUnsubscribed', handleTrackUnsubscribed);
  }

  onReconnecting(cb: () => void) {
    const listener = (error: unknown) => {
      logger.error(error);
      cb();
    };

    this.room?.on('reconnecting', listener);
  }

  onReconnected(cb: () => void) {
    this.room?.on('reconnected', cb);
  }

  sendDataTrackMessage(type: DataTrackMessageType, data: Record<string, unknown>) {
    if (!this.room || !this.dataTrack) {
      return;
    }

    this.dataTrack.send(
      JSON.stringify({
        type,
        data: {
          ...(data || {}),
          sender: this.room.localParticipant.sid,
        },
      })
    );
  }

  sendChatMessage(message: ChatMessage) {
    this.sendDataTrackMessage(
      DataTrackMessageType.chat,
      message
    );
  }

  sendUserDataMessage() {
    const state = store.getState();
    const user = selectUserData(state);

    this.sendDataTrackMessage(
      DataTrackMessageType.userData,
      {
        name: user?.name,
        avatar: user?.avatar,
      }
    );
  }

  async toggleVideo(enabled: boolean): Promise<boolean> {
    if (enabled) {
      if (!this.videoTrack) {
        const newVideoTrack = await twilio.createLocalVideoTrack(
          this.devices?.currentVideoDeviceId
            ? {
              deviceId: { exact: this.devices.currentVideoDeviceId }
            } : undefined
        );

        this.localTracks.push(newVideoTrack);

        this.attachLocalVideo();

        await this.room?.localParticipant.publishTrack(newVideoTrack);

        return enabled;
      }

      await this.videoTrack.restart();
      this.videoTrack?.enable(true);
      this.attachLocalVideo();
      await wait(500);
    } else {
      this.videoTrack?.enable(false);
      this.videoTrack?.stop();
      this.detachLocalVideo();
      await wait(500);
    }

    return enabled;
  }

  toggleAudio(enabled: boolean) {
    if (this.audioTrack) {
      this.audioTrack.enable(enabled);
    }
  }

  async switchVideo(deviceId: MediaDeviceInfo['deviceId']) {
    if (!this.videoTrack) {
      return;
    }

    this.devices?.setVideoDeviceId(deviceId);
    await this.videoTrack.restart({
      deviceId: { exact: deviceId },
    });
  }

  async switchAudio(deviceId: MediaDeviceInfo['deviceId']) {
    if (!this.audioTrack) {
      return;
    }

    this.devices?.setAudioDeviceId(deviceId);
    await this.audioTrack.restart({
      deviceId: { exact: deviceId },
    });
  }

  attachLocalVideo() {
    if (!this.localElement || !this.videoTrack?.isEnabled) {
      return;
    }

    const attached = this.videoTrack?.attach();
    if (attached) {
      this.localElement.querySelector('video')?.remove();
      this.localElement.append(attached);
    }
  }

  detachLocalVideo() {
    if (!this.localElement) {
      return;
    }

    this.localElement.querySelector('video')?.remove();
  }

  attachScreenTrack() {
    setTimeout(() => {
      if (this.remoteScreenTrack) {
        const screenshareElement = document.getElementById(this.screenShareElementId);
        if (screenshareElement) {
          screenshareElement.appendChild(this.remoteScreenTrack.attach());
        } else {
          this.remoteScreenTrack = null;
        }
      }
    }, 100);
  }

  detachScreenTrack() {
    const screenshareElement = document.getElementById(this.screenShareElementId);
    if (screenshareElement) {
      screenshareElement.querySelector('video')?.remove();
    }

    this.remoteScreenTrack = null;
  }

  attachRemoteTrack(params: AttachRemoteTrackParams) {
    switch (params.track.kind) {
      case 'video': {
        if (params.track.name.includes(SCREEN_SHARE_TRACK_PREFIX)) {
          this.remoteScreenTrack = params.track;
          params.screenCb?.();
          this.attachScreenTrack();
          return;
        }

        if (!params.videoWrapperElement) {
          return;
        }

        params.track.on('disabled', () => {
          params?.cb('video', false);
        });
        params.track.on('enabled', () => {
          params?.cb('video', true);
        });

        params?.cb('video', !!params.track?.isEnabled);
        params.videoWrapperElement.appendChild(params.track.attach());
        break;
      }
      case 'audio': {
        params.track.on('disabled', () => {
          params?.cb('audio', false);
        });
        params.track.on('enabled', () => {
          params?.cb('audio', true);
        });
        const audioElement = params.track.attach();
        audioElement.setAttribute('data-sid', params.sid);
        audioElement.autoplay = true;
        document.body.appendChild(audioElement);
        params.audioCb?.(audioElement);
        break;
      }
      case 'data':
        params.track.on('message', (data) => {
          params.dataCb?.(parseJson(data));
        });
        setTimeout(() => {
          this.sendUserDataMessage();
        }, 100);
        break;
      default:
        break;
    }
  }

  detachRemoteTrack(track: twilio.RemoteTrack) {
    if (isMediaTrack(track)) {
      track.detach().forEach((el) => el.remove());
    }
  }

  async startShareScreen(onStopShareScreen: () => void) {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      audio: false,
    });
    const track = stream.getVideoTracks()[0];

    if (track) {
      this.screenTrack = new twilio.LocalVideoTrack(track, {
        name: SCREEN_SHARE_TRACK_PREFIX,
      });

      this.screenTrack.on('stopped', () => {
        onStopShareScreen();
        this.stopShareScreen();
      });

      await this.room?.localParticipant.publishTrack(this.screenTrack);

      return this.screenTrack;
    }

    return null;
  }

  stopShareScreen() {
    if (this.room && this.screenTrack) {
      this.room.localParticipant.unpublishTrack(this.screenTrack);
      this.screenTrack.stop();
      this.screenTrack = null;
    }
  }

  destroyAudio() {
    this.audioTrack?.stop();
    this.localTracks = this.localTracks.filter((item) => item.kind !== 'audio');
  }

  reset() {
    this.stopShareScreen();
    this.videoTrack?.stop();
    this.audioTrack?.stop();
    this.localTracks = [];
    this.dataTrack = null;
  }

  destroy() {
    this.room?.disconnect();
    this.reset();
    this.room = null;
  }
}

export const call = new LocalCall();